<template>
  <div class="profile-page page-max-width footer-max-width">
    <QHeader class="bg-white text-primary">
      <HeaderImageTitle :photo="profile.getPhoto()">
        <QToolbar color="transparent">
          <QBtn v-if="!isProfileReadOnly" round color="white" icon="icon-f-x" dense flat @click="goToMainDashboard" />
          <QBtn v-if="isProfileReadOnly" round color="white" icon="icon-f-arrow-left" dense flat @click="goToMainDashboard" />
          <QToolbarTitle>{{ profile.getEtabName() }}</QToolbarTitle>
          <template v-if="isProfileReadOnly">
            <ExtraQFileBtn :ref="FILE_INPUT_REFERENCE" style="height: 50px; width: 30px" @update:model-value="onPictureChoice" @rejected="onRejected">
              <QBtn round color="white" icon="icon-f-camera" class="no-pointer-events" dense flat />
            </ExtraQFileBtn>
          </template>
        </QToolbar>
      </HeaderImageTitle>
    </QHeader>

    <QPageContainer>
      <QPage>
        <ProfileFormComponent v-if="!isProfileReadOnly" :profile="profile" @on-save-quit="onSaveQuit" />

        <ProfileReadOnlyComponent v-if="isProfileReadOnly" :profile="profile" @pass-profile-to-write-mode="passProfileToWriteMode" />
      </QPage>
    </QPageContainer>
  </div>
</template>

<script>
  import { mapState } from "pinia";
  import { useProfileStore } from "@etab/stores/profile";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { ScrollManager } from "@core/managers/scroll.manager";
  import { EtabProfileService } from "@core/services";
  import { Camera } from "@core/plugins/mobile";
  import HeaderImageTitle from "@etab/components/dashboard/HeaderImageTitle.component.vue";
  import ProfileFormComponent from "@etab/components/account/Profile.form.component.vue";
  import ProfileReadOnlyComponent from "@etab/components/account/Profile.readonly.component.vue";
  import { IEtabProfile } from "@core/models";
  import { ACTION_PROFILE_INIT, ACTION_ACCOUNT_INIT, GETTER_PROFILE_GET, GETTER_PROFILE_GET_ACCOUNT } from "@etab/stores/types";

  import { ETAB_ROUTES } from "@etab/config";
  const FILE_INPUT_REFERENCE = "FILE_INPUT";
  export default {
    components: {
      HeaderImageTitle,
      ProfileFormComponent,
      ProfileReadOnlyComponent,
    },
    data() {
      return {
        isProfileReadOnly: true,
        isPasswordReadOnly: true,
        isAccountReadOnly: true,
        modalOpened: false,
      };
    },
    computed: {
      ...mapState(useProfileStore, {
        profile: GETTER_PROFILE_GET,
        account: GETTER_PROFILE_GET_ACCOUNT,
      }),
    },
    created() {
      this.IEtabProfile = IEtabProfile;
      this.profileInit();
      this.FILE_INPUT_REFERENCE = FILE_INPUT_REFERENCE;
    },
    methods: {
      async profileInit() {
        const profileStore = useProfileStore();
        try {
          this.$_startGlobalLoading();
          await Promise.all([profileStore[ACTION_PROFILE_INIT](), profileStore[ACTION_ACCOUNT_INIT]()]);
        } catch (error) {
          NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      passProfileToWriteMode() {
        this.isProfileReadOnly = false;
        ScrollManager.scrollToTop();
      },
      goToPasswordWriteMode() {
        this.isPasswordReadOnly = false;
      },
      passAccountToWriteMode() {
        this.isAccountReadOnly = false;
      },
      async onSaveQuit({ profile }) {
        try {
          this.$_startGlobalLoading();

          await EtabProfileService.update({ profile });
          NotificationsManager.success({ message: "SUCCESS.@SAVE_SUCCESS" });
          this.goToMainDashboard();
        } catch (error) {
          NotificationsManager.error({ message: "ERRORS.@UNSAVED_MODIFICATIONS" });
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      goToMainDashboard() {
        this.$router.push({ name: ETAB_ROUTES.userMenu.root.name }).catch((suppressedError) => {});
      },
      // TODO REMOVE OPEN GALLERY
      async openGallery() {
        try {
          // we reduce quality for performance because the picture will be small
          const cameraOptions = { quality: 50 };

          const image = await Camera.getPhoto(cameraOptions);
          const sanitizedImage = Camera.sanitizeBase64(image.base64String);

          this.profile[IEtabProfile.photo] = `data:image/jpeg;base64,${sanitizedImage}`;
          this.$router.push({ name: ETAB_ROUTES.account.profile.cropper.name }).catch((suppressedError) => {});
        } catch (error) {
          // do not show error here because it could be from any reason
          // for instance just closing the modal on desktop will create an error "cancelled by user"
          throw error;
        }
      },
      async onPictureChoice(file) {
        try {
          this.$_startGlobalLoading();
          const base64String = await Camera.getBase64FromFile(file);
          const sanitizedImage = Camera.sanitizeBase64(base64String);

          this.profile[IEtabProfile.photo] = sanitizedImage;
          this.$router.push({ name: ETAB_ROUTES.account.profile.cropper.name }).catch((suppressedError) => {});
        } catch (error) {
          NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      onRejected(rejectedEntries) {
        NotificationsManager.error({ message: "ERRORS.@TRYING_TO_UPLOAD_WRONG_TYPE" });
      },
    },
  };
</script>

<style lang="scss">
  .profile-page {
    .q-page {
      padding: 15px;
    }

    h4 {
      color: $primary-lighter;
    }
  }
</style>
